import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Form as AntForm,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
} from 'antd';
import { History, Save, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import dayjs from 'dayjs';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { StatusBadge } from '@shared/components/StatusBadge';
import { useConfirm, usePrompt } from '@shared/components/ConfirmDialog';
import { sssApi } from '@api/sss';
import { categoriesApi } from '@api/categories';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';
import { useAuth } from '@features/auth/hooks/useAuth';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { VariantPanel } from '@shared/components/VariantPanel/VariantPanel';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { SssVersionsDrawer } from '../components/SssVersionsDrawer';
import {
  toContentStatus,
  useSssStatusLabel,
  type CreateSssInput,
  type SssFaq,
  type SssStatus,
  type UpdateSssInput,
} from '../types';

function makeSchema(t: Translator) {
  return z.object({
    question: z
      .string()
      .min(5, t('validation.questionMin'))
      .max(300, t('validation.questionMax')),
    // No hard upper bound — backend accepts long answers.
    answer: z.string().min(20, t('validation.answerMin')),
    categoryId: z.string().nullable(),
    order: z
      .number()
      .int()
      .min(0, t('validation.orderMin'))
      .max(9999),
    changeNote: z.string().max(200).optional(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

function emptyValues(): Values {
  return { question: '', answer: '', categoryId: null, order: 0, changeNote: '' };
}

function toValues(faq: SssFaq): Values {
  return {
    question: faq.question,
    answer: faq.answer,
    categoryId: faq.categoryId,
    order: faq.order,
    changeNote: '',
  };
}

export function SssEditPage() {
  const { t } = useTranslation(['sss', 'common', 'validation', 'errors']);
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  // Varyant ekleme akışı: create formuna ?groupId=&lang= ile gelinir.
  const variantGroupId = searchParams.get('groupId') ?? undefined;
  const variantLang = searchParams.get('lang') ?? undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const promptDialog = usePrompt();

  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);
  const statusLabel = useSssStatusLabel();
  const { user } = useAuth();
  // BE policy `content.publish` is admin-only in the seed; gate publish-grade
  // workflow actions (approve/publish/archive) on the admin role, mirroring the
  // Article/Page edit screens.
  const isAdmin = user?.role === 'ADMIN';
  const { data: activeLanguages = [] } = useActiveLanguages();
  // Seçili içerik dili (create) — effect'siz türetme (set-state-in-effect'ten kaçınır).
  const [pickedLanguageId, setPickedLanguageId] = useState<string | undefined>(variantLang);
  const languageId = pickedLanguageId ?? pickDefaultLanguage(activeLanguages)?.id;

  const canCreate = useCan(PERMISSIONS.SSS_CREATE);
  const canUpdate = useCan(PERMISSIONS.SSS_UPDATE);
  const canDelete = useCan(PERMISSIONS.SSS_DELETE);

  const [versionsOpen, setVersionsOpen] = useState(false);

  const { data: faq, isLoading } = useQuery({
    queryKey: ['sss', id],
    queryFn: () => sssApi.get(id ?? ''),
    enabled: !isNew,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', { scope: 'SSS' }],
    queryFn: () => categoriesApi.list({ scope: 'SSS' }),
    // categories module is not yet on real BE; tolerate failure silently.
    retry: false,
  });

  const initialValues = useMemo<Values>(
    () => (faq ? toValues(faq) : emptyValues()),
    [faq],
  );

  const createMutation = useMutation({
    mutationFn: (input: CreateSssInput) => sssApi.create(input),
    onSuccess: async (created) => {
      toast.success(t('toast.created'));
      await qc.invalidateQueries({ queryKey: ['sss'] });
      void recordAuditAction({
        action: 'sss.create',
        resource: 'sss',
        resourceId: created.id,
        summary: `${created.question} sorusu oluşturuldu`,
      });
      void navigate(ROUTES.SSS_EDIT.replace(':id', created.id));
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: UpdateSssInput }) =>
      sssApi.update(vars.id, vars.input),
    onSuccess: async (updated) => {
      toast.success(t('toast.updated'));
      await qc.invalidateQueries({ queryKey: ['sss'] });
      await qc.invalidateQueries({ queryKey: ['sss', updated.id] });
      await qc.invalidateQueries({ queryKey: ['sss-versions', updated.id] });
      void recordAuditAction({
        action: 'sss.update',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} güncellendi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const invalidateFaq = async (updated: SssFaq) => {
    await qc.invalidateQueries({ queryKey: ['sss', updated.id] });
    await qc.invalidateQueries({ queryKey: ['sss'] });
    await qc.invalidateQueries({ queryKey: ['sss-versions', updated.id] });
  };

  const submitReviewMutation = useMutation({
    mutationFn: () => sssApi.submitReview(id ?? ''),
    onSuccess: async (updated) => {
      toast.success(t('toast.submittedReview'));
      await invalidateFaq(updated);
      void recordAuditAction({
        action: 'sss.review.submit',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} incelemeye gönderildi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const approveMutation = useMutation({
    mutationFn: () => sssApi.approve(id ?? '', true),
    onSuccess: async (updated) => {
      toast.success(t('toast.approved'));
      await invalidateFaq(updated);
      void recordAuditAction({
        action: 'sss.review.approve',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} onaylandı`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (note: string) => sssApi.approve(id ?? '', false, note),
    onSuccess: async (updated) => {
      toast.success(t('toast.rejected'));
      await invalidateFaq(updated);
      void recordAuditAction({
        action: 'sss.review.reject',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} reddedildi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: () => sssApi.publish(id ?? '', true),
    onSuccess: async (updated) => {
      toast.success(t('toast.published'));
      await invalidateFaq(updated);
      void recordAuditAction({
        action: 'sss.publish',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} yayınlandı`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: () => sssApi.archive(id ?? ''),
    onSuccess: async (updated) => {
      toast.success(t('toast.archived'));
      await invalidateFaq(updated);
      void recordAuditAction({
        action: 'sss.archive',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} arşivlendi`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const mutationsBusy =
    submitReviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: () => sssApi.remove(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.removed'));
      await qc.invalidateQueries({ queryKey: ['sss'] });
      void recordAuditAction({
        action: 'sss.delete',
        resource: 'sss',
        resourceId: id,
        summary: `${faq?.question ?? 'SSS'} silindi`,
      });
      void navigate(ROUTES.SSS);
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const onSubmit = (values: Values) => {
    if (isNew) {
      createMutation.mutate({
        question: values.question.trim(),
        answer: values.answer.trim(),
        categoryId: values.categoryId,
        order: values.order,
        languageId: languageId ?? null,
        groupId: variantGroupId ?? null,
      });
    } else {
      updateMutation.mutate({
        id: id ?? '',
        input: {
          question: values.question.trim(),
          answer: values.answer.trim(),
          categoryId: values.categoryId,
          order: values.order,
          changeNote: values.changeNote?.trim() || undefined,
        },
      });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!isNew && !faq) {
    return (
      <div>
        <PageHeader
          title={t('edit.notFoundTitle')}
          breadcrumb={[
            { label: t('common:labels.home'), to: '/' },
            { label: 'SSS', to: ROUTES.SSS },
            { label: t('edit.notFoundBreadcrumb') },
          ]}
        />
        <Alert
          type="warning"
          showIcon
          message={t('edit.notFoundMessage')}
          action={
            <Button size="small" onClick={() => void navigate(ROUTES.SSS)}>
              {t('edit.backToList')}
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = isNew ? canCreate : canUpdate;
  const currentStatus: SssStatus = faq?.status ?? 'DRAFT';
  const approvalAtRisk = currentStatus === 'APPROVED' || currentStatus === 'IN_REVIEW';

  return (
    <div>
      <PageHeader
        title={isNew ? t('edit.newTitle') : faq?.question ?? t('edit.editTitleFallback')}
        description={
          isNew
            ? t('edit.newDescription')
            : t('edit.orderDescription', { order: faq?.order ?? 0 })
        }
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: 'SSS', to: ROUTES.SSS },
          { label: isNew ? t('edit.breadcrumbNew') : t('edit.breadcrumbEdit') },
        ]}
        actions={
          <Space wrap>
            <Button
              type="primary"
              icon={<Save size={14} />}
              htmlType="submit"
              form="sss-edit-form"
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={!canEdit}
            >
              {isNew ? t('edit.createDraft') : t('common:actions.save')}
            </Button>
          </Space>
        }
      />

      <Form
        id="sss-edit-form"
        schema={schema}
        defaultValues={initialValues}
        onSubmit={onSubmit}
        options={{ values: initialValues }}
      >
        {({ watch, setValue, formState }) => {
          const answer = watch('answer');
          const categoryId = watch('categoryId');
          const order = watch('order');

          return (
            <div className="sss-edit__grid">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!isNew && approvalAtRisk ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('common:contentWorkflow.approvalInvalidationTitle')}
                    description={t('common:contentWorkflow.approvalInvalidationDescription')}
                  />
                ) : null}
                <Card title={t('edit.basicInfo')}>
                  <AntForm layout="vertical" component="div">
                    <FormField name="question" label={t('edit.questionLabel')} required>
                      <Input
                        size="large"
                        maxLength={300}
                        showCount
                        placeholder={t('edit.questionPlaceholder')}
                      />
                    </FormField>
                  </AntForm>
                </Card>

                <Card title={t('edit.answerTitle')}>
                  <Input.TextArea
                    value={answer}
                    autoSize={{ minRows: 6, maxRows: 24 }}
                    onChange={(e) =>
                      setValue('answer', e.target.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    placeholder={t('edit.answerPlaceholder')}
                  />
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: 'var(--ds-text-secondary)',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{t('edit.charCount', { chars: (answer?.length ?? 0).toLocaleString('tr-TR') })}</span>
                    {formState.errors.answer ? (
                      <span style={{ color: 'var(--ds-danger)' }}>
                        {formState.errors.answer.message as string}
                      </span>
                    ) : null}
                  </div>
                </Card>

                {!isNew ? (
                  <Card title={t('edit.changeNoteTitle')}>
                    <FormField name="changeNote">
                      <Input
                        maxLength={200}
                        showCount
                        placeholder={t('edit.changeNotePlaceholder')}
                      />
                    </FormField>
                  </Card>
                ) : null}
              </div>

              <aside className="sss-edit__aside">
                {isNew ? (
                  <Card size="small" title={t('common:contentLanguage.label')}>
                    <LanguagePicker
                      value={languageId}
                      onChange={setPickedLanguageId}
                      disabled={Boolean(variantLang)}
                      style={{ width: '100%' }}
                    />
                    {variantGroupId ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                        {t('variant.addingHint')}
                      </div>
                    ) : null}
                  </Card>
                ) : faq ? (
                  <VariantPanel
                    currentLanguageId={faq.languageId}
                    variantLanguageCodes={faq.variantLanguageCodes}
                    contentType="sss"
                    groupId={faq.groupId}
                    onOpenVariant={(variantId) =>
                      void navigate(ROUTES.SSS_EDIT.replace(':id', variantId))
                    }
                    onAddVariant={(lang) =>
                      void navigate(`${ROUTES.SSS_NEW}?groupId=${faq.groupId}&lang=${lang.id}`)
                    }
                  />
                ) : null}
                <Card size="small" title={t('statusPanel.title')}>
                  {!isNew && faq ? (
                    <div style={{ marginBottom: 12 }}>
                      <StatusBadge
                        kind="content"
                        status={toContentStatus(currentStatus)}
                        size="sm"
                      />
                      <span
                        style={{
                          fontSize: 12,
                          marginLeft: 8,
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        {statusLabel(currentStatus)}
                      </span>
                    </div>
                  ) : null}
                  {isNew ? (
                    <Alert
                      type="info"
                      showIcon
                      message={t('statusPanel.draftAlertMessage')}
                      description={t('statusPanel.draftAlertDescription', {
                        status: statusLabel('DRAFT'),
                      })}
                    />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {currentStatus === 'DRAFT' || currentStatus === 'REJECTED' ? (
                        <Button
                          block
                          type="primary"
                          loading={submitReviewMutation.isPending}
                          onClick={() => submitReviewMutation.mutate()}
                          disabled={mutationsBusy || !canUpdate}
                        >
                          {t('statusPanel.submitReview')}
                        </Button>
                      ) : null}

                      {currentStatus === 'IN_REVIEW' && isAdmin ? (
                        <>
                          <Button
                            block
                            type="primary"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate()}
                            disabled={mutationsBusy}
                          >
                            {t('statusPanel.approve')}
                          </Button>
                          <Button
                            block
                            danger
                            loading={rejectMutation.isPending}
                            onClick={() => {
                              void (async () => {
                                const note = await promptDialog({
                                  title: t('statusPanel.rejectConfirmTitle'),
                                  description: t('statusPanel.rejectConfirmDescription'),
                                  label: t('statusPanel.rejectPrompt'),
                                  defaultValue: t('statusPanel.rejectPromptDefault'),
                                  okText: t('statusPanel.rejectConfirmOk'),
                                  danger: true,
                                  requiredMessage: t('toast.rejectReasonRequired'),
                                  maxLength: 500,
                                });
                                if (note !== null) rejectMutation.mutate(note);
                              })();
                            }}
                            disabled={mutationsBusy}
                          >
                            {t('statusPanel.reject')}
                          </Button>
                        </>
                      ) : null}

                      {(currentStatus === 'APPROVED' ||
                        currentStatus === 'DRAFT' ||
                        currentStatus === 'ARCHIVED') &&
                      isAdmin ? (
                        <Button
                          block
                          type="primary"
                          loading={publishMutation.isPending}
                          onClick={() => publishMutation.mutate()}
                          disabled={mutationsBusy}
                        >
                          {t('statusPanel.publish')}
                        </Button>
                      ) : null}

                      {currentStatus === 'PUBLISHED' && isAdmin ? (
                        <Button
                          block
                          loading={archiveMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('statusPanel.archiveConfirmTitle'),
                                description: t('statusPanel.archiveConfirmDescription', {
                                  question: faq?.question,
                                }),
                              });
                              if (ok) archiveMutation.mutate();
                            })();
                          }}
                          disabled={mutationsBusy}
                        >
                          {t('statusPanel.archive')}
                        </Button>
                      ) : null}

                      {!isAdmin &&
                      currentStatus !== 'DRAFT' &&
                      currentStatus !== 'REJECTED' ? (
                        <Alert type="warning" showIcon message={t('statusPanel.adminOnly')} />
                      ) : null}
                    </Space>
                  )}
                </Card>

                <Card size="small" title={t('edit.categoryOrderTitle')}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                      <div style={{ marginBottom: 6, fontSize: 13 }}>{t('edit.categoryLabel')}</div>
                      <Select<string | null>
                        style={{ width: '100%' }}
                        allowClear
                        placeholder={t('edit.categoryPlaceholder')}
                        value={categoryId}
                        onChange={(v) =>
                          setValue('categoryId', v ?? null, {
                            shouldDirty: true,
                          })
                        }
                        options={(categoriesData?.items ?? []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6, fontSize: 13 }}>{t('edit.orderLabel')}</div>
                      <InputNumber
                        min={0}
                        max={9999}
                        value={order}
                        onChange={(v) =>
                          setValue('order', typeof v === 'number' ? v : 0, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        style={{ width: '100%' }}
                      />
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        {t('edit.orderHint')}
                      </div>
                    </div>
                  </Space>
                </Card>

                {!isNew && faq ? (
                  <Card size="small" title={t('edit.helpfulTitle')}>
                    <Space direction="vertical" size={6} style={{ width: '100%', fontSize: 12 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>
                          <ThumbsUp
                            size={14}
                            style={{ verticalAlign: -2, marginRight: 6 }}
                          />
                          {t('edit.yes')}
                        </span>
                        <strong>{faq.helpfulYesCount.toLocaleString('tr-TR')}</strong>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>
                          <ThumbsDown
                            size={14}
                            style={{ verticalAlign: -2, marginRight: 6 }}
                          />
                          {t('edit.no')}
                        </span>
                        <strong>{faq.helpfulNoCount.toLocaleString('tr-TR')}</strong>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        <span>{t('common:labels.updatedAt')}</span>
                        <span>{dayjs(faq.updatedAt).format('DD.MM.YYYY HH:mm')}</span>
                      </div>
                      {faq.publishedAt ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            color: 'var(--ds-text-secondary)',
                          }}
                        >
                          <span>{t('edit.publishedAt')}</span>
                          <span>{dayjs(faq.publishedAt).format('DD.MM.YYYY HH:mm')}</span>
                        </div>
                      ) : null}
                    </Space>
                  </Card>
                ) : null}

                {!isNew && faq ? (
                  <Card size="small" title={t('edit.actionsTitle')}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Button
                        block
                        icon={<History size={14} />}
                        onClick={() => setVersionsOpen(true)}
                      >
                        {t('edit.versionHistory')}
                      </Button>
                      {canDelete ? (
                        <Button
                          block
                          danger
                          icon={<Trash2 size={14} />}
                          loading={deleteMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('edit.deleteTitle'),
                                description: t('edit.deleteConfirm', { question: faq.question }),
                                okText: t('common:actions.delete'),
                              });
                              if (ok) deleteMutation.mutate();
                            })();
                          }}
                        >
                          {t('common:actions.delete')}
                        </Button>
                      ) : null}
                      <Button block onClick={() => void navigate(ROUTES.SSS)}>
                        {t('edit.backToList')}
                      </Button>
                    </Space>
                  </Card>
                ) : null}

                {isNew ? (
                  <Card size="small">
                    <Alert
                      type="info"
                      showIcon
                      message={t('edit.draftInfoMessage')}
                      description={t('edit.draftInfoDescription', { status: t('common:contentStatus.DRAFT') })}
                    />
                  </Card>
                ) : null}
              </aside>
            </div>
          );
        }}
      </Form>

      {!isNew && id ? (
        <SssVersionsDrawer
          open={versionsOpen}
          faqId={id}
          currentAnswer={faq?.answer ?? ''}
          onClose={() => setVersionsOpen(false)}
        />
      ) : null}
    </div>
  );
}
